import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { mergeCachedFiltersRespectingVacancySkills } from '../utils/mergeCachedFiltersSkills';
import {
  getSerializableFiltersForSource,
  getSavedFiltersForSource as readSavedFiltersForSource,
  getSourceBucketsStorageKey,
  saveFiltersForSource as persistFiltersForSource,
} from '../../sources/filterPolicy';

// Ключи, которые не попадают в URL (слишком длинные, вызывают 431)
const URL_EXCLUDED_KEYS = new Set(['vacancyDescription']);
// Контекстные query-параметры не являются фильтрами, но должны сохраняться в URL.
const URL_CONTEXT_KEYS = new Set(['positionId']);

export const useSearchState = (defaultFilters, vacancyId = null, options = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const canSyncUrlForSource = options.canSyncUrlForSource || (() => true);

  // Determine storage and keys based on vacancyId
  const storage = vacancyId ? localStorage : sessionStorage;
  const SEARCH_STATE_KEY = vacancyId ? `searchState_v${vacancyId}` : 'searchState';
  const RESULTS_CACHE_KEY = vacancyId ? `searchResultsCache_v${vacancyId}` : 'searchResultsCache';
  const SOURCE_BUCKETS_KEY = getSourceBucketsStorageKey(SEARCH_STATE_KEY);
  const serializeFilters = (nextFilters) => getSerializableFiltersForSource(
    nextFilters?.source || defaultFilters?.source || 'hh',
    nextFilters
  );
  const buildUrlParams = (serializableFilters) => {
    const params = new URLSearchParams();
    Object.entries(serializableFilters).forEach(([key, value]) => {
      if (URL_EXCLUDED_KEYS.has(key)) return;
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, JSON.stringify(value));
          }
        } else if (typeof value === 'object') {
          params.set(key, JSON.stringify(value));
        } else {
          params.set(key, value.toString());
        }
      }
    });
    if (vacancyId) {
      params.set('positionId', String(vacancyId));
    } else {
      for (const [key, value] of searchParams.entries()) {
        if (URL_CONTEXT_KEYS.has(key) && value) {
          params.set(key, value);
        }
      }
    }
    return params;
  };
  const canSyncUrl = (nextFilters) => {
    const source = nextFilters?.source || defaultFilters?.source || 'hh';
    return canSyncUrlForSource(source, nextFilters);
  };
  const persistSearchState = (nextFilters) => {
    const serializableFilters = serializeFilters(nextFilters);
    storage.setItem(SEARCH_STATE_KEY, JSON.stringify({
      filters: serializableFilters,
      timestamp: Date.now()
    }));
    persistFiltersForSource(storage, SOURCE_BUCKETS_KEY, nextFilters);
    return serializableFilters;
  };

  // Функция для парсинга параметров из URL
  const parseUrlFilters = () => {
    const urlFilters = {};

    // Парсим все параметры из URL
    for (let [key, value] of searchParams.entries()) {
      if (URL_CONTEXT_KEYS.has(key)) {
        continue;
      }
      try {
        // Пробуем распарсить как JSON для массивов и объектов
        if (value.startsWith('[') || value.startsWith('{')) {
          urlFilters[key] = JSON.parse(value);
        } else if (value === 'true' || value === 'false') {
          urlFilters[key] = value === 'true';
        } else if (!isNaN(value)) {
          urlFilters[key] = Number(value);
        } else {
          urlFilters[key] = value;
        }
      } catch {
        urlFilters[key] = value;
      }
    }

    return urlFilters;
  };

  // TASKNEIROKLYUCH-668: нормализация старых сохранённых фильтров после изменения UI.
  // - excludeJumpers/strictRegionSearch удалены из модалки фильтров, но поля
  //   остались в FiltersState (нужны бэкенду / AI-парсеру). Сбрасываем явно,
  //   чтобы старые сохранённые значения не применялись незаметно для пользователя.
  // - hasSalary/incomeSpecified обновляются параллельно одним чекбоксом, но в
  //   старых сохранённых фильтрах они могли разойтись. Выравниваем: если хоть
  //   одно true — оба true.
  const normalizeLegacyFilters = (filters) => {
    if (!filters) return filters;
    const salary = !!(filters.hasSalary || filters.incomeSpecified);
    return {
      ...filters,
      hasSalary: salary,
      incomeSpecified: salary,
      excludeJumpers: false,
      strictRegionSearch: false,
    };
  };

  // Инициализация фильтров
  const initializeFilters = (options = {}) => {
    // 1. Сначала проверяем URL параметры (они имеют приоритет, если это прямая ссылка)
    const urlFilters = parseUrlFilters();
    const hasUrlParams = !options.ignoreUrl && Object.keys(urlFilters).length > 0;

    // 2. Проверяем хранилище
    const savedState = storage.getItem(SEARCH_STATE_KEY);
    let cachedFilters = null;
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        cachedFilters = parsed.filters;
      } catch (e) {
        console.error('Error parsing saved search state:', e);
      }
    }

    // Приоритет: URL -> Cache -> Default
    if (hasUrlParams) {
       return normalizeLegacyFilters({ ...defaultFilters, ...urlFilters });
    }

    if (cachedFilters) {
      return normalizeLegacyFilters(mergeCachedFiltersRespectingVacancySkills(defaultFilters, cachedFilters));
    }

    // 3. Используем значения по умолчанию
    return defaultFilters;
  };

  const [filters, setFiltersInternal] = useState(initializeFilters);
  const [cachedResults, setCachedResults] = useState(null);

  // Re-initialize filters when vacancyId changes
  const [prevVacancyId, setPrevVacancyId] = useState(vacancyId);
  if (prevVacancyId !== vacancyId) {
    setPrevVacancyId(vacancyId);
    setFiltersInternal(initializeFilters({ ignoreUrl: true }));
  }

  // Загружаем кешированные результаты при монтировании (или смене vacancyId)
  useEffect(() => {
    const cached = storage.getItem(RESULTS_CACHE_KEY);
    if (cached) {
      try {
        setCachedResults(JSON.parse(cached));
      } catch (e) {
        console.error('Error parsing cached results:', e);
      }
    } else {
        setCachedResults(null);
    }
  }, [RESULTS_CACHE_KEY, storage]);

  const setFilters = (newFilters) => {
    setFiltersInternal(newFilters);
    const serializableFilters = persistSearchState(newFilters);

    const isOnSearchPage = window.location.pathname.startsWith('/recruiter/search');
    if (isOnSearchPage && canSyncUrl(newFilters)) {
      const params = buildUrlParams(serializableFilters);
      setSearchParams(params);
    }
  };

  useEffect(() => {
    const serializableFilters = persistSearchState(filters);
    if (!window.location.pathname.startsWith('/recruiter/search')) return;
    if (!canSyncUrl(filters)) return;

    const params = buildUrlParams(serializableFilters);
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);

  const getSavedFiltersForSource = (source) => (
    readSavedFiltersForSource(storage, SOURCE_BUCKETS_KEY, source)
  );

  const saveFiltersForSource = (sourceOrFilters, maybeFilters) => {
    const filtersToSave = maybeFilters
      ? { ...maybeFilters, source: sourceOrFilters }
      : sourceOrFilters;
    persistFiltersForSource(storage, SOURCE_BUCKETS_KEY, filtersToSave);
  };

  // Сохраняем результаты поиска
  const cacheResults = (results, options = {}) => {
    if (results) {
      let existingCache = null;
      try {
        const raw = storage.getItem(RESULTS_CACHE_KEY);
        existingCache = raw ? JSON.parse(raw) : null;
      } catch {
        existingCache = null;
      }

      const strippedResults = results.items
        ? {
            ...results,
            items: results.items.map(({ aiComment, aiRating, fromCache, scoredAt, matchLevel, ...rest }) => rest)
          }
        : results;

      const cacheData = {
        ...(existingCache || {}),
        results: strippedResults,
        filters: serializeFilters(filters),
        timestamp: Date.now(),
        isRelevantFiltered: !!options.isRelevantFiltered,
        relevancePreset: options.relevancePreset || null,
        jumperCursors: options.jumperCursors || null,
        avitoCursors: options.avitoCursors || null
      };
      storage.setItem(RESULTS_CACHE_KEY, JSON.stringify(cacheData));
      setCachedResults(cacheData);
    }
  };

  // Обновляем отдельный элемент в кэше (например, при открытии контактов)
  const updateCacheItem = (itemId, updates) => {
    if (!cachedResults || !cachedResults.results) return;

    const updatedResults = {
      ...cachedResults.results,
      items: cachedResults.results.items.map(item =>
        item.id === itemId ? { ...item, ...updates } : item
      )
    };

    const newCacheData = {
      results: updatedResults,
      filters: cachedResults.filters,
      timestamp: cachedResults.timestamp
    };

    storage.setItem(RESULTS_CACHE_KEY, JSON.stringify(newCacheData));
    setCachedResults(newCacheData);
  };

  // Проверяем, нужно ли использовать кеш
  const shouldUseCachedResults = () => {
    if (!cachedResults) return false;

    // Проверяем, совпадают ли фильтры
    const filtersMatch = JSON.stringify(cachedResults.filters) === JSON.stringify(serializeFilters(filters));

    if (vacancyId) {
        // Для вакансии TTL не проверяем
        return filtersMatch;
    }

    // Для остальных случаев проверяем TTL (5 минут)
    const cacheAge = Date.now() - cachedResults.timestamp;
    const isFresh = cacheAge < 5 * 60 * 1000;

    return filtersMatch && isFresh;
  };

  // Очищаем состояние
  const clearSearchState = () => {
    storage.removeItem(SEARCH_STATE_KEY);
    storage.removeItem(RESULTS_CACHE_KEY);
    storage.removeItem(SOURCE_BUCKETS_KEY);
    setCachedResults(null);
    setSearchParams(new URLSearchParams());
  };

  return {
    filters,
    setFilters,
    cachedResults: shouldUseCachedResults() ? cachedResults.results : null,
    cachedResultsPayload: shouldUseCachedResults() ? cachedResults : null,
    cacheResults,
    getSavedFiltersForSource,
    saveFiltersForSource,
    updateCacheItem,
    clearSearchState
  };
};
